/**
 * Form Parser - Parses markdown with custom form directives
 *
 * Supported syntax:
 *
 * Inline format:
 * - Headers: ### Daily check-in
 * - Checkboxes: - [ ] I worked out today
 * - Range fields: @field mood type=range min=1 max=10 label="Mood"
 * - Number fields: @field weight type=number unit=kg optional
 * - Counter fields: @field water type=counter min=0 max=20 label="Glasses of water"
 * - Goal fields: @goalfield steps goal=10000 unit=steps label="Daily steps"
 * - Text areas: @text notes rows=3 placeholder="Anything worth noting?"
 * - Math: $inline$ or $$block$$
 *
 * Block format (YAML-like):
 * @field
 *   id: calories
 *   kind: number
 *   label: Calories
 *   units: kcal
 *
 * @goalfield
 *   id: water
 *   goal: 8
 *   unit: glasses
 *   label: Water intake
 *
 * @computed
 *   id: calories_7d_avg
 *   label: 7-day average calories
 *   kind: rolling_avg
 *   source: calories
 *   window: last_n_days(7)
 *   format: number(0)
 */

export type FieldKind = 'range' | 'number' | 'text' | 'counter' | 'checkbox';

export type ComputedKind = 'rolling_avg' | 'window_agg' | 'delta' | 'compliance' | 'streak' | 'expr' | 'status';

export type FormElement =
  | { type: 'header'; level: number; text: string; id: string }
  | { type: 'checkbox'; text: string; id: string; index: number }
  | { type: 'field'; fieldType: FieldKind; id: string; label: string; min?: number; max?: number; unit?: string; optional?: boolean; defaultValue?: number }
  | { type: 'goalfield'; id: string; label: string; goal: number; unit?: string; optional?: boolean; defaultValue?: number }
  | { type: 'textarea'; id: string; rows: number; placeholder?: string; label?: string; optional?: boolean }
  | { type: 'computed'; id: string; label: string; kind: ComputedKind; source?: string; sources?: string[]; window?: string; agg?: string; goal?: string; format?: string; unit?: string; missing?: string; normalize?: string; mode?: string; predicate?: string; expected?: string; achieved?: string; basis?: string; reducer?: string; output?: string; states?: string[]; rules?: ComputedRule[] }
  | { type: 'math'; content: string; inline: boolean; id: string }
  | { type: 'text'; content: string; id: string };

export interface ComputedRule {
  when?: string;
  then?: string;
  else?: string;
}

export interface FormSchema {
  elements: FormElement[];
  checkboxCount: number;
  fieldCount: number;
  computedCount: number;
}

/**
 * Parse YAML-like block properties
 * Returns object with key-value pairs and the number of lines consumed
 */
function parseBlockProperties(lines: string[], startIndex: number): { props: Record<string, any>; linesConsumed: number } {
  const props: Record<string, any> = {};
  let i = startIndex;

  while (i < lines.length) {
    const line = lines[i];
    // Check if this line is indented (part of the block)
    if (!line.match(/^\s+/) || line.trim() === '') {
      break;
    }

    const trimmed = line.trim();

    // Handle rules array for status computed fields
    if (trimmed === 'rules:') {
      const rules: ComputedRule[] = [];
      i++;
      while (i < lines.length) {
        const ruleLine = lines[i];
        if (!ruleLine.match(/^\s{4,}/) && !ruleLine.match(/^\s+-/)) {
          break;
        }
        const ruleContent = ruleLine.trim();
        if (ruleContent.startsWith('- when:')) {
          const rule: ComputedRule = {};
          const whenMatch = ruleContent.match(/^- when:\s*(.+)$/);
          if (whenMatch) {
            rule.when = whenMatch[1];
          }
          // Check next line for 'then:'
          i++;
          if (i < lines.length) {
            const thenLine = lines[i].trim();
            const thenMatch = thenLine.match(/^then:\s*(.+)$/);
            if (thenMatch) {
              rule.then = thenMatch[1];
            }
          }
          rules.push(rule);
        } else if (ruleContent.startsWith('- else:')) {
          const elseMatch = ruleContent.match(/^- else:\s*(.+)$/);
          if (elseMatch) {
            rules.push({ else: elseMatch[1] });
          }
          i++;
        } else {
          i++;
        }
      }
      props.rules = rules;
      continue;
    }

    // Handle states array
    if (trimmed.startsWith('states:')) {
      const statesMatch = trimmed.match(/^states:\s*\[([^\]]+)\]$/);
      if (statesMatch) {
        props.states = statesMatch[1].split(',').map(s => s.trim());
      }
      i++;
      continue;
    }

    // Handle sources array
    if (trimmed.startsWith('sources:')) {
      const sourcesMatch = trimmed.match(/^sources:\s*\[([^\]]+)\]$/);
      if (sourcesMatch) {
        props.sources = sourcesMatch[1].split(',').map(s => s.trim());
      }
      i++;
      continue;
    }

    // Parse key: value
    const propMatch = trimmed.match(/^(\w+):\s*(.*)$/);
    if (propMatch) {
      const key = propMatch[1];
      let value = propMatch[2];

      // Handle quoted strings
      if (value.startsWith('"') && value.endsWith('"')) {
        value = value.slice(1, -1);
      } else if (value.startsWith("'") && value.endsWith("'")) {
        value = value.slice(1, -1);
      }

      props[key] = value;
    }

    i++;
  }

  return { props, linesConsumed: i - startIndex };
}

/**
 * Parse @field directive (inline format)
 * Examples:
 *   @field mood type=range min=1 max=10 label="Mood"
 *   @field weight type=number unit=kg optional
 *   @field water type=counter min=0 max=20 label="Glasses of water"
 */
function parseFieldDirectiveInline(line: string, elementId: string): FormElement | null {
  const match = line.match(/^@field\s+(\w+)\s+(.+)$/);
  if (!match) return null;

  const id = match[1];
  const attrsString = match[2];

  // Parse attributes
  const attrs: Record<string, string> = {};

  // Match key="value" or key=value or standalone flags
  const attrRegex = /(\w+)(?:=(?:"([^"]*)"|(\S+)))?/g;
  let attrMatch;
  while ((attrMatch = attrRegex.exec(attrsString)) !== null) {
    const key = attrMatch[1];
    const value = attrMatch[2] || attrMatch[3] || 'true';
    attrs[key] = value;
  }

  const fieldType = (attrs.type as FieldKind) || 'text';
  const label = attrs.label || id;
  const optional = attrs.optional === 'true' || attrs.hasOwnProperty('optional');

  const element: FormElement = {
    type: 'field',
    fieldType,
    id,
    label,
    optional,
  };

  if (attrs.min) element.min = parseFloat(attrs.min);
  if (attrs.max) element.max = parseFloat(attrs.max);
  if (attrs.unit) element.unit = attrs.unit;
  if (attrs.default) element.defaultValue = parseFloat(attrs.default);

  return element;
}

/**
 * Parse @field block format
 */
function parseFieldBlock(props: Record<string, any>): FormElement | null {
  if (!props.id) return null;

  const fieldType = (props.kind || props.type || 'text') as FieldKind;
  const label = props.label || props.id;

  const element: FormElement = {
    type: 'field',
    fieldType,
    id: props.id,
    label,
    optional: props.optional === 'true' || props.optional === true,
  };

  if (props.min) element.min = parseFloat(props.min);
  if (props.max) element.max = parseFloat(props.max);
  if (props.unit || props.units) element.unit = props.unit || props.units;
  if (props.default) element.defaultValue = parseFloat(props.default);

  return element;
}

/**
 * Parse @goalfield directive (inline format)
 * Examples:
 *   @goalfield water goal=8 unit=glasses label="Water intake"
 *   @goalfield steps goal=10000 unit=steps
 */
function parseGoalFieldDirectiveInline(line: string, elementId: string): FormElement | null {
  const match = line.match(/^@goalfield\s+(\w+)\s+(.+)$/);
  if (!match) return null;

  const id = match[1];
  const attrsString = match[2];

  // Parse attributes
  const attrs: Record<string, string> = {};
  const attrRegex = /(\w+)(?:=(?:"([^"]*)"|(\S+)))?/g;
  let attrMatch;
  while ((attrMatch = attrRegex.exec(attrsString)) !== null) {
    const key = attrMatch[1];
    const value = attrMatch[2] || attrMatch[3] || 'true';
    attrs[key] = value;
  }

  const goal = parseFloat(attrs.goal || '0');
  const label = attrs.label || id;
  const optional = attrs.optional === 'true' || attrs.hasOwnProperty('optional');

  return {
    type: 'goalfield',
    id,
    label,
    goal,
    unit: attrs.unit,
    optional,
    defaultValue: attrs.default ? parseFloat(attrs.default) : undefined,
  };
}

/**
 * Parse @goalfield block format
 */
function parseGoalFieldBlock(props: Record<string, any>): FormElement | null {
  if (!props.id) return null;

  return {
    type: 'goalfield',
    id: props.id,
    label: props.label || props.id,
    goal: parseFloat(props.goal || '0'),
    unit: props.unit || props.units,
    optional: props.optional === 'true' || props.optional === true,
    defaultValue: props.default ? parseFloat(props.default) : undefined,
  };
}

/**
 * Parse @computed inline format
 * Example:
 *   @computed id="days_met_goal" kind="streak" source="steps" mode="threshold" output=current
 */
function parseComputedInline(line: string): FormElement | null {
  // Match @computed followed by attributes
  if (!line.startsWith('@computed ')) return null;

  const attrsString = line.slice('@computed '.length);

  // Parse attributes (key="value" or key=value)
  const attrs: Record<string, string> = {};
  const attrRegex = /(\w+)=(?:"([^"]*)"|(\S+))/g;
  let attrMatch;
  while ((attrMatch = attrRegex.exec(attrsString)) !== null) {
    const key = attrMatch[1];
    const value = attrMatch[2] || attrMatch[3];
    attrs[key] = value;
  }

  if (!attrs.id || !attrs.kind) return null;

  const element: FormElement = {
    type: 'computed',
    id: attrs.id,
    label: attrs.label || attrs.id,
    kind: attrs.kind as ComputedKind,
  };

  // Optional properties
  if (attrs.source) element.source = attrs.source;
  if (attrs.window) element.window = attrs.window;
  if (attrs.agg) element.agg = attrs.agg;
  if (attrs.goal) element.goal = attrs.goal;
  if (attrs.format) element.format = attrs.format;
  if (attrs.unit || attrs.units) element.unit = attrs.unit || attrs.units;
  if (attrs.missing) element.missing = attrs.missing;
  if (attrs.normalize) element.normalize = attrs.normalize;
  if (attrs.mode) element.mode = attrs.mode;
  if (attrs.predicate) element.predicate = attrs.predicate;
  if (attrs.expected) element.expected = attrs.expected;
  if (attrs.achieved) element.achieved = attrs.achieved;
  if (attrs.basis) element.basis = attrs.basis;
  if (attrs.reducer) element.reducer = attrs.reducer;
  if (attrs.output) element.output = attrs.output;

  return element;
}

/**
 * Parse @computed block format
 */
function parseComputedBlock(props: Record<string, any>): FormElement | null {
  if (!props.id || !props.kind) return null;

  const element: FormElement = {
    type: 'computed',
    id: props.id,
    label: props.label || props.id,
    kind: props.kind as ComputedKind,
  };

  // Optional properties
  if (props.source) element.source = props.source;
  if (props.sources) element.sources = props.sources;
  if (props.window) element.window = props.window;
  if (props.agg) element.agg = props.agg;
  if (props.goal) element.goal = props.goal;
  if (props.format) element.format = props.format;
  if (props.unit || props.units) element.unit = props.unit || props.units;
  if (props.missing) element.missing = props.missing;
  if (props.normalize) element.normalize = props.normalize;
  if (props.mode) element.mode = props.mode;
  if (props.predicate) element.predicate = props.predicate;
  if (props.expected) element.expected = props.expected;
  if (props.achieved) element.achieved = props.achieved;
  if (props.basis) element.basis = props.basis;
  if (props.reducer) element.reducer = props.reducer;
  if (props.output) element.output = props.output;
  if (props.states) element.states = props.states;
  if (props.rules) element.rules = props.rules;

  return element;
}

/**
 * Parse @text directive (inline format)
 * Examples:
 *   @text notes rows=3 placeholder="Anything worth noting?"
 */
function parseTextDirectiveInline(line: string, elementId: string): FormElement | null {
  const match = line.match(/^@text\s+(\w+)\s*(.*)$/);
  if (!match) return null;

  const id = match[1];
  const attrsString = match[2];

  // Parse attributes
  const attrs: Record<string, string> = {};
  const attrRegex = /(\w+)(?:=(?:"([^"]*)"|(\S+)))?/g;
  let attrMatch;
  while ((attrMatch = attrRegex.exec(attrsString)) !== null) {
    const key = attrMatch[1];
    const value = attrMatch[2] || attrMatch[3] || 'true';
    attrs[key] = value;
  }

  return {
    type: 'textarea',
    id,
    rows: parseInt(attrs.rows || '3', 10),
    placeholder: attrs.placeholder,
    label: attrs.label,
    optional: attrs.optional === 'true' || attrs.hasOwnProperty('optional'),
  };
}

/**
 * Parse @text block format
 */
function parseTextBlock(props: Record<string, any>): FormElement | null {
  if (!props.id) return null;

  return {
    type: 'textarea',
    id: props.id,
    rows: parseInt(props.rows || '3', 10),
    placeholder: props.placeholder,
    label: props.label,
    optional: props.optional === 'true' || props.optional === true,
  };
}

/**
 * Parse markdown form
 */
export function parseForm(markdown: string): FormSchema {
  const lines = markdown.split('\n');
  const elements: FormElement[] = [];
  let checkboxIndex = 0;
  let elementIndex = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const elementId = `elem_${elementIndex++}`;

    // Skip empty lines
    if (!trimmed) continue;

    // Headers (### Daily check-in)
    const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      elements.push({
        type: 'header',
        level: headerMatch[1].length,
        text: headerMatch[2],
        id: elementId,
      });
      continue;
    }

    // Checkboxes (- [ ] Task or - [x] Task)
    const checkboxMatch = trimmed.match(/^-\s*\[([ x])\]\s*(.*)$/);
    if (checkboxMatch) {
      elements.push({
        type: 'checkbox',
        text: checkboxMatch[2],
        id: elementId,
        index: checkboxIndex++,
      });
      continue;
    }

    // @field directive - check for block format first
    if (trimmed === '@field') {
      // Block format
      const { props, linesConsumed } = parseBlockProperties(lines, i + 1);
      const field = parseFieldBlock(props);
      if (field) {
        elements.push(field);
        i += linesConsumed;
        continue;
      }
    } else if (trimmed.startsWith('@field ')) {
      // Inline format
      const field = parseFieldDirectiveInline(trimmed, elementId);
      if (field) {
        elements.push(field);
        continue;
      }
    }

    // @goalfield directive - check for block format first
    if (trimmed === '@goalfield') {
      // Block format
      const { props, linesConsumed } = parseBlockProperties(lines, i + 1);
      const goalfield = parseGoalFieldBlock(props);
      if (goalfield) {
        elements.push(goalfield);
        i += linesConsumed;
        continue;
      }
    } else if (trimmed.startsWith('@goalfield ')) {
      // Inline format
      const goalfield = parseGoalFieldDirectiveInline(trimmed, elementId);
      if (goalfield) {
        elements.push(goalfield);
        continue;
      }
    }

    // @computed directive - check for block format first
    if (trimmed === '@computed') {
      // Block format
      const { props, linesConsumed } = parseBlockProperties(lines, i + 1);
      const computed = parseComputedBlock(props);
      if (computed) {
        elements.push(computed);
        i += linesConsumed;
        continue;
      }
    } else if (trimmed.startsWith('@computed ')) {
      // Inline format
      const computed = parseComputedInline(trimmed);
      if (computed) {
        elements.push(computed);
        continue;
      }
    }

    // @text directive - check for block format first
    if (trimmed === '@text') {
      // Block format
      const { props, linesConsumed } = parseBlockProperties(lines, i + 1);
      const textarea = parseTextBlock(props);
      if (textarea) {
        elements.push(textarea);
        i += linesConsumed;
        continue;
      }
    } else if (trimmed.startsWith('@text ')) {
      // Inline format
      const textarea = parseTextDirectiveInline(trimmed, elementId);
      if (textarea) {
        elements.push(textarea);
        continue;
      }
    }

    // Math blocks ($$...$$)
    if (trimmed.startsWith('$$') && trimmed.endsWith('$$') && trimmed.length > 4) {
      elements.push({
        type: 'math',
        content: trimmed.slice(2, -2).trim(),
        inline: false,
        id: elementId,
      });
      continue;
    }

    // Math blocks (multiline)
    if (trimmed === '$$') {
      // Find closing $$
      let mathContent = '';
      let j = i + 1;
      while (j < lines.length && lines[j].trim() !== '$$') {
        mathContent += lines[j] + '\n';
        j++;
      }
      if (j < lines.length) {
        elements.push({
          type: 'math',
          content: mathContent.trim(),
          inline: false,
          id: elementId,
        });
        i = j; // Skip to closing $$
        continue;
      }
    }

    // Inline math ($...$)
    const inlineMathMatch = trimmed.match(/\$([^$]+)\$/);
    if (inlineMathMatch) {
      // For now, treat entire line as math if it contains inline math
      // More sophisticated parsing could split text and math
      elements.push({
        type: 'math',
        content: inlineMathMatch[1],
        inline: true,
        id: elementId,
      });
      continue;
    }

    // Regular text
    elements.push({
      type: 'text',
      content: trimmed,
      id: elementId,
    });
  }

  return {
    elements,
    checkboxCount: checkboxIndex,
    fieldCount: elements.filter(e => e.type === 'field' || e.type === 'textarea' || e.type === 'goalfield').length,
    computedCount: elements.filter(e => e.type === 'computed').length,
  };
}

/**
 * Get default form data structure
 */
export function getDefaultFormData(schema: FormSchema): Record<string, unknown> {
  const data: Record<string, unknown> = {};

  schema.elements.forEach((element) => {
    if (element.type === 'checkbox') {
      data[`checkbox_${element.index}`] = false;
    } else if (element.type === 'field') {
      if (element.defaultValue !== undefined) {
        data[element.id] = element.defaultValue;
      }
    } else if (element.type === 'goalfield') {
      if (element.defaultValue !== undefined) {
        data[element.id] = element.defaultValue;
      }
    } else if (element.type === 'textarea') {
      data[element.id] = '';
    }
    // Computed fields don't have default values in user data
  });

  return data;
}
